import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  fetchNotificationPreferences, updateNotificationPreferences, fetchEmailLog,
  getAutoSync, setAutoSync, fetchGoogleCalendarStatus, setCalendarAutoAddTrusted,
} from "@/lib/api";
import { SETTINGS } from "@/constants/testIds";
import { Bell, Mail, AlertTriangle, Save, CheckCircle2, Clock, XCircle, MinusCircle, RefreshCw, Calendar, CreditCard, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const MODES = [
  {
    value: "off",
    title: "No email delivery",
    description: "I'll check KidHQ in the app. Don't email me anything.",
  },
  {
    value: "weekly_only",
    title: "Weekly briefing only",
    description: "One Sunday email summarising the week ahead. Recommended.",
  },
  {
    value: "weekly_plus_urgent",
    title: "Weekly + urgent alerts",
    description: "Weekly briefing plus same-day pings for critical items (forms due, payments overdue).",
  },
  {
    value: "daily_digest_plus_weekly",
    title: "Daily digest + weekly briefing",
    description: "A short morning digest every day and the full Sunday briefing.",
  },
];

const STATUS_PILL = {
  sent:    { color: "bg-[var(--fcc-green-soft)] text-[var(--fcc-green)]", Icon: CheckCircle2, label: "Sent" },
  stubbed: { color: "bg-[var(--fcc-cream-2)] text-[var(--fcc-ink-soft)]", Icon: Clock, label: "Stub (no real send)" },
  skipped: { color: "bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)]", Icon: MinusCircle, label: "Skipped" },
  error:   { color: "bg-[var(--fcc-red-soft)] text-[var(--fcc-red,#a23527)]", Icon: XCircle, label: "Error" },
};

function fmtDateTime(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch { return s; }
}

export default function SettingsPage() {
  const [prefs, setPrefs] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [autoSync, setAutoSyncState] = useState(null);
  const [autoSyncSaving, setAutoSyncSaving] = useState(false);
  const [gcal, setGcal] = useState(null);
  const [gcalSaving, setGcalSaving] = useState(false);

  useEffect(() => {
    Promise.all([fetchNotificationPreferences(), fetchEmailLog(), getAutoSync(), fetchGoogleCalendarStatus()])
      .then(([p, log, as, gc]) => { setPrefs(p); setEmailLog(log); setAutoSyncState(as); setGcal(gc); })
      .catch(e => setError(e?.response?.data?.detail || "Could not load settings."))
      .finally(() => setLoading(false));
  }, []);

  const toggleAutoSync = async () => {
    if (!autoSync) return;
    setAutoSyncSaving(true);
    try {
      const next = !autoSync.enabled;
      const r = await setAutoSync(next);
      setAutoSyncState((prev) => ({ ...(prev || {}), ...r }));
      toast.success(next ? "Daily Gmail auto-sync turned ON." : "Daily Gmail auto-sync turned OFF.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update auto-sync.");
    } finally {
      setAutoSyncSaving(false);
    }
  };

  const toggleGcalAutoAdd = async () => {
    if (!gcal) return;
    setGcalSaving(true);
    try {
      const next = !gcal.auto_add_trusted;
      const r = await setCalendarAutoAddTrusted(next);
      setGcal((prev) => ({ ...(prev || {}), auto_add_trusted: r.enabled }));
      toast.success(next
        ? "Trusted senders will be added to Google Calendar automatically."
        : "Trusted-sender auto-add turned OFF.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update setting.");
    } finally {
      setGcalSaving(false);
    }
  };

  const handleSave = async (newMode) => {
    setSaving(true);
    try {
      const updated = await updateNotificationPreferences({
        mode: newMode,
        email_address: prefs?.email_address || undefined,
      });
      setPrefs(updated);
      toast.success("Preferences saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-[var(--fcc-muted)]">Loading settings…</div>;
  }
  if (error) {
    return (
      <div className="fcc-card p-10 text-center">
        <AlertTriangle className="w-6 h-6 mx-auto text-[var(--fcc-orange)]" />
        <p className="mt-3 text-[var(--fcc-ink)] font-semibold">{error}</p>
      </div>
    );
  }

  return (
    <div data-testid={SETTINGS.page} className="space-y-10">
      <header className="fcc-card overflow-hidden grid lg:grid-cols-12">
        <div className="lg:col-span-8 p-8 sm:p-10">
          <div className="flex flex-wrap gap-3 items-center">
            <span className="fcc-pill fcc-chip-emma"><Bell className="w-3 h-3" /> Notification settings</span>
            <Link
              to="/app/settings/billing"
              data-testid="settings-billing-link"
              className="fcc-pill bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)] transition inline-flex items-center gap-1.5"
            >
              <CreditCard className="w-3 h-3" /> Billing & Plan <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <h1 className="font-display text-[40px] sm:text-[52px] leading-[1.05] text-[var(--fcc-ink)] mt-6">
            How should KidHQ reach you?
          </h1>
          <p className="text-[var(--fcc-ink-soft)] mt-5 max-w-2xl leading-relaxed">
            Pick how much email you want. Your Sunday family briefing is the highest-signal touchpoint — most parents keep it on.
          </p>
        </div>
        <aside className="lg:col-span-4 p-7 sm:p-8 bg-[var(--fcc-cream-2)] border-l border-[var(--fcc-border)]">
          <p className="label-overline flex items-center gap-2"><Mail className="w-3 h-3" /> Email on file</p>
          <p className="font-display text-2xl text-[var(--fcc-ink)] mt-3 break-all">{prefs?.email_address || "—"}</p>
          <p className="text-xs text-[var(--fcc-muted)] mt-2">From your Google account. We never share this address.</p>
        </aside>
      </header>

      <section>
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-5">Email frequency</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {MODES.map(m => {
            const active = prefs?.mode === m.value;
            return (
              <button
                key={m.value}
                type="button"
                data-testid={`${SETTINGS.notifModeRadio}-${m.value}`}
                onClick={() => handleSave(m.value)}
                disabled={saving}
                className={`text-left fcc-card p-6 transition border-2 disabled:opacity-50 ${active ? "border-[var(--fcc-green)] bg-[var(--fcc-green-soft)]" : "border-transparent hover:border-[var(--fcc-border)]"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-xl text-[var(--fcc-ink)]">{m.title}</h3>
                  {active && <CheckCircle2 className="w-5 h-5 text-[var(--fcc-green)] shrink-0" />}
                </div>
                <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 leading-relaxed">{m.description}</p>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-[var(--fcc-muted)] mt-4 flex items-center gap-2">
          <Save className="w-3 h-3" /> Saved automatically on selection.
        </p>
      </section>

      <section data-testid="settings-auto-sync-section">
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-5">Gmail auto-sync</h2>
        <div className="fcc-card p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-4 max-w-2xl">
            <span className="w-10 h-10 rounded-xl bg-[var(--fcc-green-soft)] flex items-center justify-center shrink-0">
              <RefreshCw className="w-4 h-4 text-[var(--fcc-green)]" />
            </span>
            <div>
              <p className="font-display text-lg text-[var(--fcc-ink)]">Auto-sync Gmail daily</p>
              <p className="text-sm text-[var(--fcc-ink-soft)] mt-1 leading-relaxed">
                When ON, KidHQ pulls new kids-only emails once every 24h. Uses the same trusted-sender filter as manual sync. Skips when Gmail is disconnected or the AI classifier is over budget.
              </p>
              {autoSync?.last_run && (
                <p className="text-xs text-[var(--fcc-muted)] mt-2">
                  Last run: {new Date(autoSync.last_run).toLocaleString()}
                  {autoSync.last_status ? ` · ${autoSync.last_status}` : ""}
                </p>
              )}
            </div>
          </div>
          <button
            data-testid="auto-sync-toggle"
            onClick={toggleAutoSync}
            disabled={!autoSync || autoSyncSaving}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition disabled:opacity-50 ${autoSync?.enabled ? "bg-[var(--fcc-green)]" : "bg-[var(--fcc-border)]"}`}
            aria-pressed={!!autoSync?.enabled}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${autoSync?.enabled ? "translate-x-8" : "translate-x-1"}`} />
          </button>
        </div>
      </section>

      <section data-testid="settings-google-calendar-section">
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-5">Google Calendar</h2>
        <div className="fcc-card p-6 space-y-4">
          <div className="flex items-start gap-4">
            <span className="w-10 h-10 rounded-xl bg-[var(--fcc-emma)] flex items-center justify-center shrink-0">
              <Calendar className="w-4 h-4 text-[var(--fcc-green)]" />
            </span>
            <div className="flex-1">
              <p className="font-display text-lg text-[var(--fcc-ink)]">
                Calendar status ·{" "}
                {gcal?.can_write ? <span className="text-[var(--fcc-green)]">connected (read + write)</span>
                  : gcal?.can_read ? <span className="text-[var(--fcc-green)]">read only</span>
                  : gcal?.connected ? <span className="text-amber-700">needs reconnect</span>
                  : <span className="text-[var(--fcc-muted)]">not connected</span>}
              </p>
              <p className="text-sm text-[var(--fcc-ink-soft)] mt-1 leading-relaxed max-w-2xl">
                Connect by clicking <strong>Connect Gmail</strong> on the Inbox page — same OAuth flow grants both Gmail and Calendar scopes.
                {gcal?.connected && !gcal?.can_write && " Existing Gmail users: reconnect to grant the new Calendar scope."}
              </p>
            </div>
          </div>
          {gcal?.can_write && (
            <div className="rounded-xl border border-[var(--fcc-border)] p-4 flex items-start justify-between gap-4 bg-[var(--fcc-cream)]/40">
              <div className="max-w-2xl">
                <p className="font-semibold text-sm text-[var(--fcc-ink)]">Trusted-sender auto-add</p>
                <p className="text-xs text-[var(--fcc-ink-soft)] mt-1 leading-relaxed">
                  When ON, KidHQ auto-creates Google Calendar events from emails sent by trusted senders (TeamSnap, ParentSquare, ClassDojo, SchoolMessenger, school .edu / .k12 domains). Everything else still requires your approval click.
                </p>
              </div>
              <button
                data-testid="gcal-auto-add-toggle"
                onClick={toggleGcalAutoAdd}
                disabled={gcalSaving}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition disabled:opacity-50 ${gcal?.auto_add_trusted ? "bg-[var(--fcc-green)]" : "bg-[var(--fcc-border)]"}`}
                aria-pressed={!!gcal?.auto_add_trusted}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${gcal?.auto_add_trusted ? "translate-x-8" : "translate-x-1"}`} />
              </button>
            </div>
          )}
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-5">Recent email activity</h2>
        {emailLog.length === 0 ? (
          <div className="fcc-card p-8 text-center text-sm text-[var(--fcc-muted)]">
            No emails sent yet. Send your first weekly briefing from the Weekly Briefing page.
          </div>
        ) : (
          <ul className="space-y-2">
            {emailLog.map(e => {
              const pill = STATUS_PILL[e.status] || STATUS_PILL.skipped;
              const Icon = pill.Icon;
              return (
                <li
                  key={e.id}
                  data-testid={SETTINGS.emailLogRow}
                  className="border border-[var(--fcc-border)] rounded-xl p-4 flex flex-wrap items-center gap-3 bg-white"
                >
                  <span className={`fcc-pill ${pill.color}`}><Icon className="w-3 h-3" /> {pill.label}</span>
                  <span className="text-sm font-semibold text-[var(--fcc-ink)] flex-1 min-w-[160px]">{e.kind?.replace(/_/g, " ")}</span>
                  <span className="text-xs text-[var(--fcc-muted)]">{e.trigger}</span>
                  <span className="text-xs text-[var(--fcc-muted)]">→ {e.to_email}</span>
                  <span className="text-xs text-[var(--fcc-muted)]">{fmtDateTime(e.sent_at)}</span>
                  {e.error && <span className="text-xs text-[var(--fcc-red,#a23527)] w-full mt-1">{e.error}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
